import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import type { PrizeDrawWinner } from '../hooks/usePrizeDrawPastWinners';
import { getWinnerRewardTone } from '../utils/prizeDrawUtils';

const VISIBLE_ROWS = 20;
/** Enough slides for Swiper vertical loop + autoplay to keep scrolling. */
const MIN_LOOP_SLIDES = VISIBLE_ROWS * 2;

type PastWinnersCardProps = {
  winners: PrizeDrawWinner[];
  loading: boolean;
  dropLabel: string;
};

const WinnerRow = ({ winner, subtitle }: { winner: PrizeDrawWinner; subtitle: string }) => {
  const tone = getWinnerRewardTone(winner.prize);
  const rewardClass =
    tone === 'gold' ? 'pdr-reward-gold' : tone === 'green' ? 'pdr-reward-green' : 'pdr-reward-blue';

  return (
    <div className='pdr-row pdr-row-carousel'>
      <div className='pdr-av' style={{ background: winner.color, color: winner.textColor }}>
        {winner.avatar}
      </div>
      <div className='pdr-main'>
        <div className='pdr-name'>
          {winner.name} <span>· {winner.location}</span>
        </div>
        <div className='pdr-sub'>{subtitle}</div>
      </div>
      <div className='pdr-right'>
        <span className={rewardClass}>{winner.prize}</span>
      </div>
    </div>
  );
};

const buildLoopSlides = (winners: PrizeDrawWinner[]) => {
  if (winners.length === 0) return [];
  const slides = [...winners];
  while (slides.length < MIN_LOOP_SLIDES) {
    slides.push(...winners);
  }
  return slides;
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const PastWinnersCard = ({ winners, loading }: PastWinnersCardProps) => {
  const loopSlides = buildLoopSlides(winners);

  return (
    <div style={{ padding: 0 }}>
      <div className='card pd-card'>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <span style={{ color: '#fff', fontSize: 15, fontWeight: 500 }}>Past rent drops</span>
          <span className='pdr-badge-verified'>✓ Verified</span>
        </div>
        <hr
          style={{
            border: 'none',
            borderTop: '1px solid rgba(255,255,255,.06)',
            margin: '0 0 12px',
          }}
        />

        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className='pdr-row' style={{ opacity: 0.5 }}>
              <div className='pdr-av' style={{ background: '#1e293b' }}>
                -
              </div>
              <div className='pdr-main'>
                <div className='pdr-name'>Loading…</div>
              </div>
            </div>
          ))
        ) : winners.length === 0 ? (
          <div className='pdr-row' style={{ marginBottom: 0, color: 'rgba(255,255,255,.45)' }}>
            <div className='pdr-main'>
              <div className='pdr-name'>Once the drop is completed, winners will appear here.</div>
            </div>
          </div>
        ) : (
          <Swiper
            className='pdr-winners-swiper'
            modules={[Autoplay]}
            direction='vertical'
            slidesPerView={VISIBLE_ROWS}
            spaceBetween={0}
            loop
            loopAdditionalSlides={VISIBLE_ROWS}
            speed={650}
            autoplay={
              prefersReducedMotion()
                ? false
                : {
                    delay: 2800,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                  }
            }
            allowTouchMove={false}
            style={{ touchAction: 'pan-y' }}
          >
            {loopSlides.map((winner, index) => (
              <SwiperSlide key={`${winner.id}-${index}`}>
                <WinnerRow winner={winner} subtitle={winner.subtitle} />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>
    </div>
  );
};
